import React, { useContext, useMemo } from 'react';
import { InfiniteTableProps, Table } from 'components';
import { formatPhoneNumber, formatPhoneNumberAsLink } from 'utils';
import { ContactListSortOrder } from '@bb/contacts';
import { useBreakpoint } from 'hooks';

import { LinkCell, TagsCell, NameCell, BranchCell } from './TableCells';

import { AuthContext } from '../../../../contexts';

export type BrokerageContactTableProps = Omit<InfiniteTableProps, 'columns'> & {
  sort?: {
    field: string;
    order: ContactListSortOrder;
  };
  filter?: Record<string, string[]>;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
};

const BrokerageContactTable: React.FC<BrokerageContactTableProps> = ({
  sort,
  filter,
  page,
  setPage,
  ...props
}) => {
  const { auth } = useContext(AuthContext);
  const { md } = useBreakpoint();

  const { dataSource, itemCount, onChange, onItemClick } = props;

  const PAGE_SIZE = 20;

  const FOOTER_OFFSET = 324;

  const BROKERAGE_COLUMNS = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'firstName',
        width: 220,
        sortOrder: sort?.field === 'firstName' && sort.order,
        sorter: true,
        render: (text?: string, record?: any) => (
          <NameCell
            loading={!record}
            firstName={record?.firstName}
            lastName={record?.lastName}
            avatarFilestackId={record?.avatarFilestackId}
          />
        ),
      },
      {
        title: 'Role',
        dataIndex: 'role',
        width: 100,
        sortOrder: sort?.field === 'role' && sort.order,
        sorter: true,
        filteredValue: filter?.role || null,
        filters: [
          {
            text: 'Agent',
            value: 'AGENT',
          },
          {
            text: 'Team Admin',
            value: 'TEAM_ADMIN',
          },
          {
            text: 'Branch Admin',
            value: 'BRANCH_ADMIN',
          },
          {
            text: 'Global Admin',
            value: 'GLOBAL_ADMIN',
          },
        ],
        render: (text?: string, record?: any) => {
          const roles = [record?.role?.split('_').join(' ').toLowerCase()];
          return <TagsCell loading={!record} tags={roles} />;
        },
      },
      {
        title: 'Branch',
        dataIndex: 'location',
        width: 125,
        sortOrder: sort?.field === 'location' && sort.order,
        sorter: true,
        filteredValue: filter?.location || null,
        filters: [] as Record<'text' | 'value', string>[],
        render: (text?: string, record?: any) => (
          <BranchCell
            loading={!record}
            location={record?.location}
            address={record?.address}
            type="name"
          />
        ),
      },
      {
        title: 'Office',
        dataIndex: 'location',
        width: 125,
        render: (text?: string, record?: any) => (
          <BranchCell
            loading={!record}
            location={record?.location}
            address={record?.address}
            type="phone"
          />
        ),
      },
      {
        title: 'Mobile',
        dataIndex: 'location',
        width: 125,
        render: (text?: string, record?: any) => (
          <LinkCell
            loading={!record}
            value={record?.phone && formatPhoneNumber(record?.phone)}
            linktext={formatPhoneNumberAsLink(record?.phone)}
            visible={record?.frontEndInstructions?.releaseCell}
          />
        ),
      },
      {
        title: 'Email',
        dataIndex: 'email',
        width: 200,
        render: (text?: string, record?: any) => (
          <LinkCell loading={!record} value={record?.email} type="email" />
        ),
      },
    ],
    [sort],
  );

  const branchColumnIndex = BROKERAGE_COLUMNS.findIndex(
    ({ title }) => title === 'Branch',
  );

  BROKERAGE_COLUMNS[branchColumnIndex].filters =
    auth &&
    Object.entries(auth.org.locations).map(([location, { name }]) => ({
      text: name,
      value: location,
    }));

  return (
    <Table
      dataSource={dataSource}
      columns={BROKERAGE_COLUMNS}
      pagination={{
        position: ['bottomCenter'],
        total: itemCount,
        pageSize: PAGE_SIZE,
        showSizeChanger: false,
        onChange: newPage => setPage(newPage),
      }}
      scroll={{
        x: md ? 'max-content' : '100%',
        y: `calc(100vh - ${FOOTER_OFFSET}px)`,
      }}
      onRow={(contact, index) => ({
        onClick: () => onItemClick(index, contact),
        style: { cursor: 'pointer' },
      })}
      showSorterTooltip={false}
      onChange={onChange}
    />
  );
};

export default BrokerageContactTable;
